import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  Interaction,
  TooltipItem,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useMemo } from 'react'
import { getRelativePosition } from 'chart.js/helpers'
import { hoverBackgroundPlugin } from './plugins/hoverBackgroundPlugin'
import { yAxisLabelPlugin } from './plugins/yAxisLabelPlugin'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)
ChartJS.register(hoverBackgroundPlugin, yAxisLabelPlugin)

// @ts-expect-error something weird with chartjs types
Tooltip.positioners.top = function (items) {
  // @ts-expect-error something weird with chartjs types
  const pos = Tooltip.positioners.average(items)

  if (pos === false) {
    return false
  }

  // @ts-expect-error something weird with chartjs types
  const chart = this.chart

  return {
    x: pos.x,
    y: chart.chartArea.top - 40,
    xAlign: 'center',
    yAlign: 'top',
  }
}

// @ts-expect-error something weird with chartjs types
Interaction.modes.xAxisSingle = function (chart, e, _, useFinalPosition) {
  const position = getRelativePosition(e, chart)
  const items: {
    element: unknown
    datasetIndex: number
    index: number
  }[] = []
  Interaction.evaluateInteractionItems(
    chart,
    'x',
    position,
    (element, datasetIndex, index) => {
      if (element.inXRange(position.x, useFinalPosition)) {
        items.push({ element, datasetIndex, index })
      }
    },
  )
  return items.length > 0 ? [items[0]] : []
}

ChartJS.defaults.font.family =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
ChartJS.defaults.font.weight = 'bold'

export interface BarGraphDataset {
  data: (number | null)[]
  backgroundColor?: string
  borderRadius?: number
  borderSkipped?: boolean
  barThickness?: 'flex'
}

export interface BarGraphProps {
  labels: string[]
  datasets: BarGraphDataset[]
  chartHeight?: number
  yAxisLabel?: string
  yAxisValues: number[]
  maxYAxisValue?: number
  toolTipCallback?: (context: TooltipItem<'bar'>) => string
  xAxisTicksCallback?: (value: string | number) => string
}

export function BarGraph(props: BarGraphProps) {
  const { labels, datasets, yAxisValues, yAxisLabel } = props

  const data = useMemo(
    () =>
      ({
        labels: labels,
        datasets: datasets.map((dataset) => ({
          data: dataset.data,
          backgroundColor: dataset.backgroundColor ?? '#00922B',
          borderRadius: dataset.borderRadius ?? 8,
          borderSkipped: dataset.borderSkipped ?? false,
          barThickness: dataset.barThickness ?? 'flex',
        })),
      } as ChartData<'bar'>),
    [labels, datasets],
  )

  const options = useMemo(
    () =>
      ({
        responsive: true,
        maintainAspectRatio: false,
        skipNull: true,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          // @ts-expect-error custom plugin option for yAxisLabel
          yAxisLabel: {
            label: yAxisLabel,
          },
          tooltip: {
            enabled: true,
            // we know top exists as a position option
            position: 'top' as 'nearest',
            caretSize: 0,
            animation: false,
            backgroundColor: '#F2F2F2',
            titleColor: '#5D5D5D',
            bodyColor: '#5D5D5D',
            borderColor: '#F2F2F2',
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title: () => '',
              label: props.toolTipCallback ?? (() => ''),
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'xAxisSingle' as 'x',
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
            ticks: {
              color: '#5d5d5d',
              maxRotation: 0,
              autoSkip: false,
            },
          },
          y: {
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
            max: props.maxYAxisValue,
            ticks: {
              autoSkip: false,
              color: '#5d5d5d',
              align: 'end',
              callback: props.xAxisTicksCallback ?? (() => ''),
            },
          },
        },
      } satisfies ChartOptions<'bar'>),
    [labels, datasets, yAxisValues, yAxisLabel, props.toolTipCallback],
  )

  return (
    <div className={`h-[${props.chartHeight ?? 190}px] w-full`}>
      <Bar data={data} options={options} />
    </div>
  )
}
